import cx from 'classnames'
import { useField } from 'formik'
import { useEffect, useRef, useState } from 'react'

import { FieldWrapper, Icons } from 'src/common/components'
import { IOption, IOptionsGroup } from 'src/common/interfaces'

import BasicMultiSelectGroupsItem from './BasicMultiSelectGroupsItem'
import BasicMultiSelectGroupsSearch from './BasicMultiSelectGroupsSearch'
import styles from './basic-multi-select-groups.module.scss'

interface BasicMultiSelectGroupsProps {
  name: string
  searchable?: boolean
  placeholder?: string
  groups: IOptionsGroup[]
  options: IOption[]
  onChange?: (value: string[]) => void
  required?: boolean
  className?: string
  label?: string
}

const BasicMultiSelectGroups = (props: BasicMultiSelectGroupsProps) => {
  const {
    name,
    groups,
    options,
    searchable,
    placeholder = '',
    onChange,
    ...rest
  } = props

  const [{ value: fieldValue }, , { setValue }] = useField(name)

  const [isOpen, setIsOpen] = useState<boolean>(false)
  const [activeGroup, setActiveGroup] = useState<string | null>(null)
  const [searchValue, setSearchValue] = useState<string>('')
  const selectRef = useRef(null)

  useEffect(() => {
    if (selectRef.current) {
      const clickEventHandler = (event: MouseEvent | TouchEvent) => {
        const specifiedElement = selectRef.current
        if (specifiedElement && event.target instanceof Node) {
          const isClickInside = (specifiedElement as HTMLDivElement).contains(
            event.target,
          )

          if (!isClickInside) {
            setIsOpen(false)
          }
        }
      }

      const keyDownEventHandler = (event: KeyboardEvent) => {
        if (event.key === 'Escape') {
          setIsOpen(false)
        }
      }

      window.addEventListener('mousedown', clickEventHandler, false)
      window.addEventListener('touchstart', clickEventHandler, false)
      window.addEventListener('keydown', keyDownEventHandler, false)

      return () => {
        window.removeEventListener('mousedown', clickEventHandler, false)
        window.removeEventListener('touchstart', clickEventHandler, false)
        window.removeEventListener('keydown', keyDownEventHandler, false)
      }
    }
  }, [selectRef])

  const handleOpen = () => {
    if (isOpen) {
      setActiveGroup(null)
      setSearchValue('')
    }

    setIsOpen(!isOpen)
  }

  const handleChange = async (newValue: string) => {
    //onChange(newValue) //TODO implement onChange
    if (fieldValue?.includes(newValue)) {
      setValue(fieldValue?.filter((value: string) => value !== newValue))
      return
    }

    setValue([...(fieldValue || []), newValue])
  }

  const currentOptionsLabel =
    options
      ?.filter((option: IOption) => fieldValue?.includes(option.value))
      .map((option: IOption) => option.label)
      .join(', ') ?? ''

  return (
    <FieldWrapper name={name} {...rest} withError={false}>
      <div className={styles.wrapper}>
        <div
          //className={cx(styles.input, styles.selectWithoutBorder)}
          className={styles.input}
          ref={selectRef}
          data-name={name}
          onClick={handleOpen}
        >
          {currentOptionsLabel ? (
            <div>{currentOptionsLabel}</div>
          ) : (
            <div className={styles.placeholder}>{placeholder}</div>
          )}

          <div className={cx(styles.arrow, isOpen && styles.open)}>
            <Icons.ArrowSelect />
          </div>

          {isOpen ? (
            <div
              className={styles.groupsWrapper}
              onClick={(e) => e.stopPropagation()}
            >
              {searchable ? (
                <BasicMultiSelectGroupsSearch
                  currentValue={fieldValue || []}
                  searchValue={searchValue}
                  options={options}
                  onSearchChange={setSearchValue}
                  onChange={handleChange}
                />
              ) : null}

              {!searchValue ? (
                <>
                  {groups.map((group) => (
                    <BasicMultiSelectGroupsItem
                      key={group.id}
                      showGroup={groups.length > 1}
                      currentValue={fieldValue || []}
                      isActive={activeGroup === group.id}
                      {...group}
                      options={options}
                      onToggle={() =>
                        setActiveGroup(
                          activeGroup !== group.id ? group.id : null,
                        )
                      }
                      onChange={handleChange}
                    />
                  ))}
                </>
              ) : null}
            </div>
          ) : null}
        </div>
      </div>
    </FieldWrapper>
  )
}

export default BasicMultiSelectGroups
